/**
 * AbsencesTab - Onglet Absences et Congés
 * Gestion des demandes d'absence et soldes de congés
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import { Checkbox } from "../../ui/checkbox";
import { Progress } from "../../ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../ui/tabs";
import { 
  Plus, CalendarDays, Calendar, Clock, FileText, 
  Check, X, AlertCircle, Loader2, Briefcase, Palmtree,
  ThermometerSun, Baby, GraduationCap, Home, Trash2, Edit2,
  CheckCircle, XCircle, HourglassIcon
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import {
  ABSENCE_TYPE_OPTIONS,
  ABSENCE_STATUS_OPTIONS,
  getAbsenceTypeLabel,
  getAbsenceStatusLabel,
  getAbsenceTypeColor,
  getAbsenceStatusColor,
} from "../../../lib/hr-constants";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for absence types
const ABSENCE_ICONS = {
  paid_leave: Palmtree,
  rtt: Clock,
  unpaid_leave: Briefcase,
  sick_leave: ThermometerSun,
  work_accident: AlertCircle,
  maternity: Baby,
  paternity: Baby,
  parental: Baby,
  family_event: CalendarDays,
  training: GraduationCap,
  remote_work: Home,
  other: FileText,
};

export default function AbsencesTab({ employee }) {
  const [absences, setAbsences] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  
  // Filter state
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState("");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    absence_type: "",
    start_date: "",
    end_date: "",
    start_half_day: false,
    end_half_day: false,
    reason: "",
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    absence_type: "",
    start_date: "",
    end_date: "",
    start_half_day: false,
    end_half_day: false,
    status: "",
    reason: "",
    manager_comment: "",
  });

  const token = localStorage.getItem("token");

  const fetchAbsences = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterYear) params.append("year", filterYear);
      if (filterStatus) params.append("status", filterStatus);
      
      const response = await axios.get(
        `${API}/api/hr/employees/${employee.id}/absences?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAbsences(response.data.absences || []);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des absences");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [employee.id, token, filterYear, filterStatus]);

  const fetchBalance = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/api/hr/employees/${employee.id}/leave-balance?year=${filterYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBalance(response.data);
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  }, [employee.id, token, filterYear]);

  useEffect(() => {
    fetchAbsences();
    fetchBalance();
  }, [fetchAbsences, fetchBalance]);

  const handleAddAbsence = async () => {
    if (!form.absence_type || !form.start_date || !form.end_date) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employee.id}/absences`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddDialog(false);
      setForm({
        absence_type: "",
        start_date: "",
        end_date: "",
        start_half_day: false,
        end_half_day: false,
        reason: "",
      });
      fetchAbsences();
      fetchBalance();
    } catch (err) {
      console.error("Error creating absence:", err);
      if (err.response?.data?.detail) {
        alert(err.response.data.detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditAbsence = async () => {
    if (!selectedAbsence) return;
    
    setSaving(true);
    try {
      const updateData = {};
      Object.keys(editForm).forEach(key => {
        if (editForm[key] !== "" && editForm[key] !== null && editForm[key] !== undefined) {
          updateData[key] = editForm[key];
        }
      });
      
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/absences/${selectedAbsence.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditDialog(false);
      setSelectedAbsence(null);
      fetchAbsences();
      fetchBalance();
    } catch (err) {
      console.error("Error updating absence:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAbsence = async () => {
    if (!selectedAbsence) return;
    
    setSaving(true);
    try {
      await axios.delete(
        `${API}/api/hr/employees/${employee.id}/absences/${selectedAbsence.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteDialog(false);
      setSelectedAbsence(null);
      fetchAbsences();
      fetchBalance();
    } catch (err) {
      console.error("Error deleting absence:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickApprove = async (absence) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/absences/${absence.id}`,
        { status: "approved" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAbsences();
      fetchBalance();
    } catch (err) {
      console.error("Error approving:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickReject = async (absence) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/absences/${absence.id}`,
        { status: "rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAbsences();
    } catch (err) {
      console.error("Error rejecting:", err);
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (absence) => {
    setSelectedAbsence(absence);
    setEditForm({
      absence_type: absence.absence_type || "",
      start_date: absence.start_date || "",
      end_date: absence.end_date || "",
      start_half_day: absence.start_half_day || false,
      end_half_day: absence.end_half_day || false,
      status: absence.status || "",
      reason: absence.reason || "",
      manager_comment: absence.manager_comment || "",
    });
    setShowEditDialog(true);
  };

  // Group absences by status
  const pendingAbsences = absences.filter(a => a.status === "pending");
  const otherAbsences = absences.filter(a => a.status !== "pending");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-slate-900">Absences & Congés</h3>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-absence-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle absence
        </Button>
      </div>

      {/* Balance Cards */}
      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <BalanceCard
            title="Congés payés"
            taken={balance.paid_leave_taken}
            total={balance.paid_leave_total}
            remaining={balance.paid_leave_remaining}
            color="green"
          />
          <BalanceCard
            title="RTT"
            taken={balance.rtt_taken}
            total={balance.rtt_total}
            remaining={balance.rtt_remaining}
            color="blue"
          />
          <BalanceCard
            title="Maladie"
            taken={balance.sick_days_taken}
            total={null}
            remaining={null}
            color="red"
          />
          <BalanceCard
            title="Autres"
            taken={balance.other_days_taken}
            total={null}
            remaining={null}
            color="slate"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list">
            Liste des absences
            {pendingAbsences.length > 0 && (
              <Badge className="ml-2 bg-amber-500">{pendingAbsences.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="calendar">Calendrier</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {ABSENCE_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pending absences */}
          {pendingAbsences.length > 0 && (
            <Card className="border-amber-200 bg-amber-50/30 mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <HourglassIcon className="w-4 h-4 text-amber-600" />
                  En attente de validation ({pendingAbsences.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {pendingAbsences.map(absence => (
                    <AbsenceCard 
                      key={absence.id} 
                      absence={absence}
                      onEdit={() => openEditDialog(absence)}
                      onDelete={() => { setSelectedAbsence(absence); setShowDeleteDialog(true); }}
                      onApprove={() => handleQuickApprove(absence)}
                      onReject={() => handleQuickReject(absence)}
                      showActions
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other absences */}
          {otherAbsences.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-600" />
                  Historique ({otherAbsences.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="max-h-[400px]">
                  <div className="space-y-3">
                    {otherAbsences.map(absence => (
                      <AbsenceCard 
                        key={absence.id} 
                        absence={absence}
                        onEdit={() => openEditDialog(absence)}
                        onDelete={() => { setSelectedAbsence(absence); setShowDeleteDialog(true); }}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {absences.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="py-12">
                <div className="flex flex-col items-center text-center">
                  <CalendarDays className="w-12 h-12 text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-1">Aucune absence</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Enregistrez une absence ou un congé pour cet employé
                  </p>
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle absence
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Vue calendrier à venir dans une prochaine version</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle absence</DialogTitle>
            <DialogDescription>
              Enregistrez une absence ou une demande de congé
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type d'absence *</Label>
              <Select value={form.absence_type} onValueChange={(v) => setForm({...form, absence_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début *</Label>
                <DatePickerFr
                  value={form.start_date}
                  onChange={(v) => setForm({...form, start_date: v, end_date: form.end_date || v})}
                  placeholder="JJ/MM/AAAA"
                />
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox 
                    id="start_half"
                    checked={form.start_half_day}
                    onCheckedChange={(v) => setForm({...form, start_half_day: v})}
                  />
                  <label htmlFor="start_half" className="text-xs text-slate-500">Après-midi uniquement</label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date de fin *</Label>
                <DatePickerFr
                  value={form.end_date}
                  onChange={(v) => setForm({...form, end_date: v})}
                  placeholder="JJ/MM/AAAA"
                />
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox 
                    id="end_half"
                    checked={form.end_half_day}
                    onCheckedChange={(v) => setForm({...form, end_half_day: v})}
                  />
                  <label htmlFor="end_half" className="text-xs text-slate-500">Matin uniquement</label>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Motif / Commentaire</Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({...form, reason: e.target.value})}
                placeholder="Raison de l'absence..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAddAbsence} disabled={saving || !form.absence_type || !form.start_date || !form.end_date}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier l'absence</DialogTitle>
            <DialogDescription>
              Modifiez les détails ou le statut de l'absence
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editForm.absence_type} onValueChange={(v) => setEditForm({...editForm, absence_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ABSENCE_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ABSENCE_STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de début</Label>
                <DatePickerFr
                  value={editForm.start_date}
                  onChange={(v) => setEditForm({...editForm, start_date: v})}
                />
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox 
                    id="edit_start_half"
                    checked={editForm.start_half_day}
                    onCheckedChange={(v) => setEditForm({...editForm, start_half_day: v})}
                  />
                  <label htmlFor="edit_start_half" className="text-xs text-slate-500">Après-midi</label>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <DatePickerFr
                  value={editForm.end_date}
                  onChange={(v) => setEditForm({...editForm, end_date: v})}
                />
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox 
                    id="edit_end_half"
                    checked={editForm.end_half_day}
                    onCheckedChange={(v) => setEditForm({...editForm, end_half_day: v})}
                  />
                  <label htmlFor="edit_end_half" className="text-xs text-slate-500">Matin</label>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Motif employé</Label>
              <Textarea
                value={editForm.reason}
                onChange={(e) => setEditForm({...editForm, reason: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Commentaire manager</Label>
              <Textarea
                value={editForm.manager_comment}
                onChange={(e) => setEditForm({...editForm, manager_comment: e.target.value})}
                placeholder="Commentaire sur la décision..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleEditAbsence} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler cette absence ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'absence sera marquée comme annulée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Retour</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAbsence} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Annuler l'absence
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Balance Card Component
function BalanceCard({ title, taken, total, remaining, color }) {
  const colorClasses = {
    green: "bg-green-50 border-green-200 text-green-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    red: "bg-red-50 border-red-200 text-red-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };

  const percentage = total ? ((total - (remaining || 0)) / total) * 100 : 0;

  return (
    <Card className={`border ${colorClasses[color]}`}>
      <CardContent className="pt-4 pb-3">
        <div className="text-xs font-medium uppercase tracking-wide opacity-70">{title}</div>
        <div className="mt-1">
          {total !== null ? (
            <>
              <div className="text-2xl font-bold">{remaining ?? 0}</div>
              <div className="text-xs opacity-70">sur {total} jours</div>
              <Progress value={percentage} className="h-1.5 mt-2" />
            </>
          ) : (
            <>
              <div className="text-2xl font-bold">{taken ?? 0}</div>
              <div className="text-xs opacity-70">jour(s) pris</div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Absence Card Component
function AbsenceCard({ absence, onEdit, onDelete, onApprove, onReject, showActions }) {
  const Icon = ABSENCE_ICONS[absence.absence_type] || FileText;
  
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors group">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getAbsenceTypeColor(absence.absence_type)}`}>
        <Icon className="w-5 h-5" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-medium text-slate-900">
            {getAbsenceTypeLabel(absence.absence_type)}
          </span>
          <Badge variant="outline" className={getAbsenceStatusColor(absence.status)}>
            {getAbsenceStatusLabel(absence.status)}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {absence.days_count} jour(s)
          </Badge>
        </div>
        
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatDateFr(absence.start_date)}
            {absence.start_half_day && " (PM)"}
            {" → "}
            {formatDateFr(absence.end_date)}
            {absence.end_half_day && " (AM)"}
          </span>
        </div>
        
        {absence.reason && (
          <p className="text-sm text-slate-600 mt-1 line-clamp-1">{absence.reason}</p>
        )}
        
        {absence.manager_comment && (
          <p className="text-xs text-slate-400 mt-1 italic">
            Manager: {absence.manager_comment}
          </p>
        )}
      </div>
      
      <div className="flex gap-1">
        {showActions && absence.status === "pending" && (
          <>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
              onClick={onApprove}
              title="Approuver"
              data-testid={`approve-absence-${absence.id}`}
            >
              <CheckCircle className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={onReject}
              title="Refuser"
              data-testid={`reject-absence-${absence.id}`}
            >
              <XCircle className="w-4 h-4" />
            </Button>
          </>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 opacity-0 group-hover:opacity-100"
          onClick={onEdit}
          data-testid={`edit-absence-${absence.id}`}
        >
          <Edit2 className="w-4 h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100"
          onClick={onDelete}
          data-testid={`delete-absence-${absence.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
